import { StanButton } from '@/components';
import IcUpload from '@/assets/icons/ic-file-upload.svg';
import { ChangeEvent, useRef, useState } from 'react';
import { logWithSentry } from '@/lib';
import { uploadDisputeEvidenceFile } from '@/lib/api/income/api';

interface DisputeFileUploadProps {
    onStripeFileIdChange: (fileId?: number) => void;
}

export function DisputeFileUpload({ onStripeFileIdChange }: DisputeFileUploadProps) {
    const [fileName, setFileName] = useState('');
    const [fileImportErrorMessage, setFileImportErrorMessage] = useState('');
    const [isFileUploading, setIsFileUploading] = useState(false);
    const [stripeFileId, setStripeFileId] = useState<number | undefined>(undefined);
    const fileUploadRef = useRef<HTMLInputElement>(null);

    const isFileTooLarge = (file?: File) => {
        if (!file) return true;
        const fileSizeinMB = file.size / (1024 * 1024);
        // From testing 3.5MB is the largest file that can be uploaded alongside our evidence
        return fileSizeinMB > 3.5;
    };

    const openFileUploadDialog = () => {
        setStripeFileId(undefined);
        setFileName('');
        setIsFileUploading(false);
        setFileImportErrorMessage('');
        onStripeFileIdChange();
        fileUploadRef?.current?.click();
    };
    const uploadFile = async (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files?.length && event.target.files.length > 0) {
            const file = event.target.files[0];
            setIsFileUploading(true);
            setFileName(file?.name ?? '');

            if (isFileTooLarge(file)) {
                // Mimic Stripe error message
                setFileImportErrorMessage(
                    'The file you uploaded was too large. Please upload a file smaller than 3.5 MB'
                );
                setIsFileUploading(false);
                return;
            }

            const response = await uploadDisputeEvidenceFile(file).catch(err => {
                if (err.response.status === 400) {
                    setFileImportErrorMessage(err.response?.data?.message);
                } else {
                    setFileImportErrorMessage('There was an issue uploading your file');
                    logWithSentry('[DISPUTE-EVIDENCE-UPLOAD-FAILURE]', { context: { error: err } });
                    return;
                }
            });
            setStripeFileId(response?.file_id);
            setIsFileUploading(false);
            onStripeFileIdChange(response?.file_id);
        }
    };
    return (
        <div className='max-w-full grow basis-1 px-0'>
            <div
                className={
                    'border-neutral-subtle relative grid min-h-32 w-full rounded-lg border border-dashed md:h-full ' +
                    (stripeFileId ? 'bg-neutral-soft border-none' : '')
                }
            >
                <div className='relative size-full md:absolute'>
                    <div className='flex size-full flex-col items-center justify-center text-center'>
                        <input
                            className='hidden'
                            type='file'
                            accept='image/jpeg,image/png,application/pdf'
                            onChange={uploadFile}
                            ref={fileUploadRef}
                        />

                        {stripeFileId && <div className='text-truncate w-full p-3'>{fileName}</div>}
                        {fileImportErrorMessage && (
                            <small className='error text-negative-active p-3'>{fileImportErrorMessage}</small>
                        )}
                        <StanButton
                            name='dispute-evidence-upload-button'
                            icon={IcUpload}
                            size='xs'
                            variant='outline'
                            onClick={openFileUploadDialog}
                            loading={isFileUploading}
                        >
                            {stripeFileId ? 'Replace File' : 'Upload Evidence'}
                        </StanButton>
                    </div>
                </div>
            </div>
        </div>
    );
}
